import type {Component, Editor} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {dropRedundantStyleAttribute, setComponentStyle} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {isOfTypeTable} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {handleTablePadding} from '@EmailMakerReact/lib/email-maker-table-library';

/** Text styled larger than this is flagged as a header for email-client semantics. */
const HEADER_FONT_SIZE_THRESHOLD_PX = 20;
const DATA_IS_HEADER_ATTR = 'data-is-header';
const HEADER_FLAG_ON = '1' as const;
const HEADER_FLAG_OFF = '0' as const;

export function initStyleListeners(editor: Editor): () => void {
  const onFontSizeUpdate = (model: Component | undefined): void => {
    const component = model ?? editor.getSelected();
    const style = component?.getStyle();

    if (!component || typeof style?.['font-size'] === 'undefined') {
      return;
    }

    const fontSize = style['font-size'] as string;

    if (parseInt(fontSize, 10) > HEADER_FONT_SIZE_THRESHOLD_PX) {
      component.getEl()?.setAttribute(DATA_IS_HEADER_ATTR, HEADER_FLAG_ON);
    } else {
      component.getEl()?.setAttribute(DATA_IS_HEADER_ATTR, HEADER_FLAG_OFF);
    }
  };

  const onBorderWidthUpdate = (model: Component | undefined): void => {
    const component = model ?? editor.getSelected();
    const style = component?.getStyle();

    if (style && !style['border-style']) {
      component?.addStyle({'border-style': 'solid'});
    }
  };

  /** Resetting height to auto on a width change preserves aspect ratio; applies only to img-tagged components, not PMW_IMAGE (whose own tagName is td/div). */
  const onWidthUpdate = (model: Component | undefined): void => {
    const component = model ?? editor.getSelected();
    if (!component) {
      return;
    }
    if (component.get('tagName')?.toLowerCase() !== 'img') {
      return;
    }
    setComponentStyle(editor, {property: 'height', value: 'auto', component});
  };

  /** Mirrors a table's background-color onto the bgcolor HTML attribute for Outlook / legacy-email-client fallback; gated on tagName === 'table', not PMW type. */
  const onBgColorUpdate = (model: Component | undefined): void => {
    const component = model ?? editor.getSelected();
    if (!component) {
      return;
    }

    if (!isOfTypeTable(editor, component)) {
      return;
    }

    const bgColor = component.getStyle()['background-color'];
    if (typeof bgColor !== 'string' || bgColor === '') {
      return;
    }

    writeBgColorAttribute(component, bgColor);
  };

  const onPaddingUpdate = (component: Component): void => {
    handleTablePadding(editor, component);
  };

  const onVerticalAlignUpdate = (model: Component | undefined): void => {
    const component = model ?? editor.getSelected();
    if (!component) {
      return;
    }

    const componentType = component.get('type');
    if (componentType !== EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE && componentType !== EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION) {
      return;
    }

    const parentTable = component.getEl();
    const currentAlignment = component.getStyle()['vertical-align'];
    if (!parentTable || typeof currentAlignment !== 'string') {
      return;
    }

    parentTable.querySelectorAll('td').forEach((cell) => {
      cell.style.verticalAlign = currentAlignment;
    });
  };

  const onLoad = (): void => {
    editor.UndoManager.clear();
  };

  editor.on('component:styleUpdate:background-color', onBgColorUpdate);
  editor.on('component:styleUpdate:font-size', onFontSizeUpdate);
  editor.on('component:styleUpdate:border-width', onBorderWidthUpdate);
  editor.on('component:styleUpdate:padding', onPaddingUpdate);
  editor.on('component:styleUpdate:vertical-align', onVerticalAlignUpdate);
  editor.on('component:styleUpdate:width', onWidthUpdate);
  editor.on('load', onLoad);

  return (): void => {
    editor.off('component:styleUpdate:background-color', onBgColorUpdate);
    editor.off('component:styleUpdate:font-size', onFontSizeUpdate);
    editor.off('component:styleUpdate:border-width', onBorderWidthUpdate);
    editor.off('component:styleUpdate:padding', onPaddingUpdate);
    editor.off('component:styleUpdate:vertical-align', onVerticalAlignUpdate);
    editor.off('component:styleUpdate:width', onWidthUpdate);
    editor.off('load', onLoad);
  };
}

/**
 * Writes the bgcolor HTML attribute for Outlook / legacy-email-client fallback.
 * Drops any stale raw `style` attribute first: addAttributes triggers GrapesJS attrUpdated, which
 * full-replaces model.style from any raw `style` attribute, so skipping this step would revert the
 * just-written background-color to the loaded inline value.
 */
function writeBgColorAttribute(component: Component, bgColor: string): void {
  dropRedundantStyleAttribute(component);
  component.addAttributes({bgcolor: bgColor});
}
